import { useCallback, useState } from "react";
import { v4 } from "uuid";
import { useLazyHtzAirHandlingUnitDataListQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import { usePutAirHandlingUnitData } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-air-handling-unit-data-offline";
import {
  copyAirHandlingUnitData,
  newAirHandlingUnitData,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { AhuDataOwnerTypes } from "@/services/backend/htz/ahu/types/ahu-data-owner-types";
import { useListPosition } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-work-order-positions-offline";
import { useLazyHtzAirHandlingUnitShowQuery } from "@/services/backend/htz/ahu/api/ahu-api";

/**
 * useFindOrCreateAhuData returns a function to find or create the
 * [ahu.Data] instance for a given AirHandlingUnit in a work order.
 *
 * Within one work order, an [ahu.Data] is owned by the work order itself
 * and shared by all positions referencing the same AirHandlingUnit. This
 * hook resolves the right Data ID:
 *
 *   1. Reuse the Data ID already attached to a sibling position in the
 *      cached position list, if any.
 *   2. Otherwise, query the backend for the work order's existing Data
 *      for this AirHandlingUnit (closes the stale-cache / offline-merge
 *      gap).
 *   3. Otherwise, find the latest Data instance for this AirHandlingUnit
 *      across the system, create a new one for this work order, and
 *      pre-fill from the latest.
 *   4. Otherwise, create a blank instance using name/designation from
 *      the AirHandlingUnit itself.
 */
export function useFindOrCreateAhuData(
  workOrderId: string,
  customerId: string,
  serviceObjectId: string,
) {
  const [listAhuData] = useLazyHtzAirHandlingUnitDataListQuery();
  const [showAhu] = useLazyHtzAirHandlingUnitShowQuery();
  const [putAhuData] = usePutAirHandlingUnitData();
  const { data: positions } = useListPosition(workOrderId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const findOrCreateAhuData = useCallback(
    async (ahuId: string): Promise<string> => {
      setIsLoading(true);
      setError(null);

      try {
        // 1. Sibling cache hit.
        const siblingWithSameAhu = positions?.find(
          (p) => p.ahuId === ahuId && p.ahuDataId !== null,
        );
        if (siblingWithSameAhu) {
          return siblingWithSameAhu.ahuDataId!;
        }

        // 2. Backend lookup for this work order's Data on the AHU.
        const existingForWorkOrder = await listAhuData({
          owningWorkOrder: { active: true, values: [workOrderId] },
          airHandlingUnit: { active: true, values: [ahuId] },
          limit: 1,
        }).unwrap();
        if (existingForWorkOrder.items.length > 0) {
          return existingForWorkOrder.items[0].id;
        }

        // 3. Pre-fill from the latest Data for this AHU across the system.
        const latestForAhu = await listAhuData({
          airHandlingUnit: { active: true, values: [ahuId] },
          orderBy: [{ field: "updatedAt", dsc: true }],
          limit: 1,
        }).unwrap();

        if (latestForAhu.items.length > 0) {
          const newAhuData = copyAirHandlingUnitData(
            latestForAhu.items[0],
            workOrderId,
            AhuDataOwnerTypes.WorkOrder,
          );
          await putAhuData(newAhuData);
          return newAhuData.id;
        }

        // 4. No prior Data exists for this AHU. Create a blank instance
        //    using name/designation from the AirHandlingUnit so the user
        //    sees an identifiable entry.
        const ahu = await showAhu({ id: ahuId }).unwrap();
        const newAhuData = {
          ...newAirHandlingUnitData(
            v4(),
            ahuId,
            ahu.companyId,
            customerId,
            serviceObjectId,
            workOrderId,
            AhuDataOwnerTypes.WorkOrder,
          ),
          name: ahu.name,
          designation: ahu.designation,
        };
        await putAhuData(newAhuData);
        return newAhuData.id;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [
      positions,
      listAhuData,
      showAhu,
      customerId,
      serviceObjectId,
      workOrderId,
      putAhuData,
    ],
  );

  return { findOrCreateAhuData, isLoading, error };
}
